import {
  TOWER_KINDS,
  UNIT_KINDS,
  type AttackPlan,
  type TowerKind,
  type UnitKind,
} from "@empire/core-sim";
import palette from "./palette.json";

/**
 * Placeholder art (S6-3): a colour and one letter per thing.
 *
 * The brief is explicit — "coloured shapes with 1-letter glyphs; silhouette
 * test passed; no time spent on beauty". Colour alone is not enough: five
 * tower types at 28 pt on a phone, some of them warm browns, are not reliably
 * tellable apart, and about one man in twelve cannot separate the red from the
 * green at all. A letter is the cheapest thing that always works.
 *
 * The letters are chosen to be unique, not to be initials — Ballista and
 * Brazier both start with B, so one of them has to give. A test asserts
 * uniqueness and full coverage, because a duplicate would not look like a bug,
 * it would look like two of the same tower.
 */

export const TOWER_GLYPH: Record<TowerKind, string> = {
  arrow: "A",
  ballista: "L", // baLLista — Brazier keeps the B
  brazier: "B",
  frost: "F",
  cannon: "C",
};

export const UNIT_GLYPH: Record<UnitKind, string> = {
  militia: "M",
  shieldbearer: "S",
  rider: "R",
  ram: "A", // rAm — Rider keeps the R
  priest: "P",
  saboteur: "B", // saBoteur — Shieldbearer keeps the S
  scout: "C",
};

/**
 * Units are dots five pixels across, so a letter is not an option — at that
 * size it is a smudge. They get a SHAPE instead, which is the same idea by
 * other means: shape survives a small screen and a colour deficiency, and it
 * costs nothing, because all forty of them are still drawn into one Graphics.
 *
 * Shape carries the role (a square is armour, a triangle moves fast, a diamond
 * does something odd) and colour separates within it, so no two of the seven
 * are the same pair.
 */
export type UnitShape = "circle" | "square" | "triangle" | "diamond";

export const UNIT_SHAPE: Record<UnitKind, UnitShape> = {
  militia: "circle",
  shieldbearer: "square",
  rider: "triangle",
  ram: "square",
  priest: "diamond",
  saboteur: "triangle",
  scout: "circle",
};

/** Radius in screen pixels. The second of a shared shape is the smaller one. */
export const UNIT_RADIUS: Record<UnitKind, number> = {
  militia: 5,
  shieldbearer: 5,
  rider: 5,
  ram: 7, // the biggest thing on the board, and it should look like it
  priest: 5,
  saboteur: 3.5,
  scout: 3.5,
};

/** Deliberately far apart in hue AND in lightness, for the same reason. */
export const UNIT_TINT: Record<UnitKind, string> = {
  militia: palette.clay,
  shieldbearer: palette.iron,
  rider: palette.grain,
  ram: palette.wood,
  priest: palette.ok,
  saboteur: palette.danger,
  scout: palette["ink-dim"],
};

/**
 * Which unit each id in a replay frame is.
 *
 * Frames carry ids and positions, not types — adding the type to every frame
 * would inflate the wire format for something the client can derive. The
 * simulation numbers units 0, 1, 2… in plan order, wave by wave, so the plan
 * IS the mapping. A test pins that against the real simulation, because if
 * spawn ordering ever changed, the colours would quietly start lying.
 */
export function unitKindsFromPlan(plan: AttackPlan): UnitKind[] {
  const kinds: UnitKind[] = [];
  for (const wave of plan.waves) for (const kind of wave.units) kinds.push(kind);
  return kinds;
}

/** Every tower type has a glyph, and no two share one. */
export function glyphsAreDistinct(): boolean {
  const towers = TOWER_KINDS.map((k) => TOWER_GLYPH[k as TowerKind]);
  const units = UNIT_KINDS.map((k) => UNIT_GLYPH[k as UnitKind]);
  return new Set(towers).size === towers.length && new Set(units).size === units.length;
}

/** Every unit reads differently from every other one on the board. */
export function unitsAreDistinct(): boolean {
  const seen = UNIT_KINDS.map(
    (k) => `${UNIT_SHAPE[k as UnitKind]}/${UNIT_RADIUS[k as UnitKind]}/${UNIT_TINT[k as UnitKind]}`
  );
  return new Set(seen).size === seen.length;
}

/**
 * What each tower is FOR, in words a new player can act on. The sheet's
 * "Role" column ("Anti-light, fast") is written for balancing, not for a
 * phone screen in the middle of building a maze.
 */
export const TOWER_BLURB: Record<TowerKind, string> = {
  arrow: "Fast shots, light units",
  ballista: "Pierces armour",
  brazier: "Burns whole groups",
  frost: "Slows the fastest",
  cannon: "Heavy hits, not up close",
};
